import { useEffect } from "react";

import { AppState, useAppState } from "../../contexts/App";
import { Site } from "../../model/site";
import { api } from "../../services/api";

/**
 * Subscribing in the middle of creating/editing a Keepo: keeps that Keepo as the one being edited, so the
 * side preview shows it. After a reload the app starts over and it's no longer in memory; it's recovered
 * from `?site=` (the URL every step into the subscription builds from the editing Keepo).
 */
export const useFlowEditingSite = (
  isEditorFlow: boolean,
  siteId: string | null,
  site: Site | undefined,
  setSite: (site: Site) => void
) => {
  const { appState, setAppState } = useAppState();
  const isMissing = !site || appState.editingSiteId !== siteId;

  useEffect(() => {
    if (!isEditorFlow || !siteId || !isMissing) {
      return;
    }
    let cancelled = false;
    api
      .getSites()
      .then(sites => {
        const found = sites.find(item => item.siteId === siteId);
        if (cancelled || !found || !(found.draft || found.site)) {
          return;
        }
        setAppState({
          ...appState,
          editingSiteId: found.siteId,
          published: !!found.site,
          editingSiteDisabled: found.disabled,
          editingSiteSubscription: found.subscription || { type: "FREE" },
        } as AppState);
        setSite((found.draft || found.site) as Site);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Only when the Keepo is missing: setting it is what this does
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorFlow, siteId, isMissing]);
};
